import { motion } from 'framer-motion';
import { HelpCircle, Heart, FileCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const cprFaqs = [
  {
    question: 'How long is a CPR class?',
    answer: 'Most classes are 2-3 hours depending on the course type. One-on-one sessions may be slightly shorter, while group classes with multiple participants may take the full 3 hours.',
  },
  {
    question: "What's the difference between Heartsaver and BLS?",
    answer: 'Heartsaver courses are designed for the general public and workplace requirements. BLS (Basic Life Support) is specifically for healthcare providers and includes more advanced techniques and team dynamics. BLS is required for nurses, doctors, EMTs, and other medical professionals.',
  },
  {
    question: 'What is a blended course?',
    answer: 'A blended course allows you to complete the knowledge portion online at your own pace, then schedule a shorter in-person skills check (30-60 minutes) to demonstrate your hands-on abilities and earn your certification.',
  },
  {
    question: 'Do you travel to businesses and churches?',
    answer: 'Yes! We offer mobile training services and will come to your location. This is especially convenient for group training. We serve businesses, churches, schools, and other organizations throughout Georgia.',
  },
  {
    question: 'How long is my certification valid?',
    answer: 'American Heart Association certifications are valid for 2 years from the date of completion.',
  },
  {
    question: 'What is your refund/reschedule policy?',
    answer: 'We understand schedules change. Please contact us at least 24 hours before your scheduled class to reschedule. Refunds are available for cancellations made 48+ hours in advance.',
  },
  {
    question: 'Do I need any prior experience?',
    answer: 'No prior experience is needed for any of our courses. Our instructors will guide you through everything step by step.',
  },
];

const notaryFaqs = [
  {
    question: 'What ID is accepted for notarization?',
    answer: 'We accept valid, unexpired government-issued photo IDs such as: state driver\'s license, state ID card, U.S. passport, U.S. military ID, or U.S. permanent resident card. The ID must not be expired and the photo must be recognizable.',
  },
  {
    question: 'Should I sign the document before you arrive?',
    answer: 'No! Do NOT sign your document before the notary appointment. The notary must witness you signing the document. If the document is already signed, it cannot be notarized.',
  },
  {
    question: 'Do you notarize wills and power of attorney?',
    answer: 'I can notarize signatures on many documents including powers of attorney. However, I am not an attorney and cannot provide legal advice about what documents you need or what they mean. For wills and estate planning, please consult with an attorney first.',
  },
  {
    question: 'What are your travel fees?',
    answer: 'Travel fee starts at $25 for local appointments (within 10 miles). Extended travel is $25 + $1/mile. After-hours/weekend appointments add $20, and same-day rush appointments add $30. The state-regulated notary fee ($2 per notarial act) is separate.',
  },
  {
    question: 'Can you come to a hospital or nursing home?',
    answer: 'Yes, we provide mobile notary services to hospitals, nursing homes, rehabilitation centers, and other care facilities. Additional travel fees may apply, and after-hours fees apply for visits outside normal business hours.',
  },
  {
    question: 'What should I have ready for the appointment?',
    answer: 'Have the following ready: 1) All documents that need notarization (unsigned), 2) Valid photo ID for each signer, 3) All signers present at the appointment, 4) Knowledge of what you\'re signing.',
  },
  {
    question: 'Do you provide legal advice?',
    answer: 'No. As a notary public, I am not permitted to provide legal advice. I can only notarize documents and witness signatures. For legal questions about your documents, please consult with an attorney.',
  },
];

export default function FAQs() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <HelpCircle className="w-4 h-4" />
              <span>Frequently Asked Questions</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="faqs-title"
            >
              FAQs
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find answers to common questions about our CPR training and mobile notary services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CPR FAQs */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-[#264653]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                CPR & First Aid Training
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4" data-testid="cpr-faqs">
              {cprFaqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`cpr-${index}`}
                  className="border border-slate-200 rounded-lg px-6 data-[state=open]:bg-primary/5"
                >
                  <AccordionTrigger className="text-left font-medium text-[#264653] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Notary FAQs */}
      <section className="py-16 md:py-24 bg-[#EDF6F9]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#E29578]/10 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#E29578]" />
              </div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-[#264653]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Mobile Notary
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4" data-testid="notary-faqs">
              {notaryFaqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`notary-${index}`}
                  className="border border-slate-200 rounded-lg px-6 bg-white data-[state=open]:bg-[#E29578]/5"
                >
                  <AccordionTrigger className="text-left font-medium text-[#264653] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Still Have Questions?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              We're happy to help! Contact us directly and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:912-388-6305">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  data-testid="faq-call-btn"
                >
                  Call 912-388-6305
                </Button>
              </a>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  data-testid="faq-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
